import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import isNil from 'lodash/isNil';
import classnames from 'classnames';
import { useQuery } from '@tanstack/react-query';

import '@components/mixins/commonStyles.scss';
import styles from './ChatPreloader.module.scss';

import { EhrChatData, RecurringSubscriptionAmountCalculationResponse } from 'chaika-frontend-types/frontend-cms';
import { ChatServerEvents } from 'chaika-frontend-types/server-events';
import { ChatCreatedData } from 'chaika-frontend-types/server-events/chat';

import { ServerEventBus } from '@core/ServerEvents';

import { isDefined } from '@utils/isDefined';
import { Declension } from '@utils/Declension';

import { store } from '@store';
import { Preloader } from '@components/Preloader';
import { Button, ButtonTheme } from '@components/Button';
import { translate } from '@chaika/i18n/helper';
import { Checkbox } from '@components/Checkbox';
import { PreloaderModifiers } from '@components/Preloader/Preloader';
import { useDataLoading } from '@utils/hooks/useDataLoading';
import { RecurringSubscriptionApi } from '@api/RecurringSubscriptionApi';
import { recurringSubscriptionTrialDurationQueryOptions } from '@store/RecurringSubscription/queries/recurringSubscriptionTrialDurationQueries';
import { Money } from '@store/models/Money';
import { wait } from '@utils/wait';
import { isChatWithDoctor } from '@patient/utils/isChatWithDoctor';

interface Params {
    hideSubscriptionCost?: boolean;
    doctorId?: string;
    practiceId?: string;
    startLoader: boolean;
    children: React.ReactNode;
    updateTitle?(title: string): void;
    reloadChats(): Promise<EhrChatData[]>;
}

const TWO_SECONDS = 2 * 1000;

export const ChatPreloader = observer(
    ({
        doctorId,
        practiceId,
        startLoader,
        hideSubscriptionCost,
        updateTitle,
        reloadChats,
        children,
    }: Params): React.ReactElement<any> => {
        const {
            app,
            ehr,
            patientStore: { patientList },
        } = store;

        const [loading, setLoading] = useState(startLoader ?? false);
        const [chat, setChat] = useState<EhrChatData>();
        const [defaultBillingAmount, setDefaultBillingAmount] =
            useState<RecurringSubscriptionAmountCalculationResponse>();
        const { data: trialDuration } = useQuery(recurringSubscriptionTrialDurationQueryOptions.get());

        useDataLoading(async () => {
            if (isDefined(doctorId)) {
                try {
                    const billingAmount = await RecurringSubscriptionApi.calculateBillingAmount({
                        doctorId,
                    });
                    setDefaultBillingAmount(billingAmount);
                } catch {}
            }
        }, [doctorId]);

        useEffect(() => {
            const serverEventBus = ServerEventBus.getInstance();

            let stop = false;

            const onChatCreated = async (event: ChatCreatedData) => {
                if (event.body.patientId === ehr.id) {
                    while (!stop) {
                        await wait(TWO_SECONDS);
                        const chats = await reloadChats();

                        const chat = chats.find(chat => {
                            const isChatForDoctor =
                                isDefined(doctorId) && isChatWithDoctor(chat, doctorId) && chat.patientId === ehr.id;
                            const isChatForPractice =
                                isDefined(practiceId) && chat.practiceId === practiceId && chat.patientId === ehr.id;

                            return isChatForDoctor || isChatForPractice;
                        });

                        if (isDefined(chat)) {
                            stop = true;
                            setLoading(false);
                            setChat(chat);
                            updateTitle?.('Чат с пациентом успешно создан');
                        }
                    }
                }
            };

            serverEventBus.addEventListener(ChatServerEvents.ChatEvent.ChatCreated, onChatCreated);

            return () => {
                stop = true;
                serverEventBus.removeEventListener(ChatServerEvents.ChatEvent.ChatCreated);
            };
        }, [doctorId, practiceId, ehr.id, patientList, loading, updateTitle, reloadChats]);

        return loading || (startLoader && isNil(chat?.joinLink)) ? (
            <div className={styles.preloader}>
                <Preloader modifiers={[PreloaderModifiers.Big]} />
            </div>
        ) : isDefined(chat) ? (
            <div className={styles.preloaderRoot}>
                {!hideSubscriptionCost && (
                    <>
                        <div className={classnames([styles.text, 'font_body16Black'])}>
                            {translate('$freeChatSubscriptionsText', {
                                $cost: defaultBillingAmount
                                    ? Money.fromKopecks(
                                          defaultBillingAmount.amount,
                                          defaultBillingAmount.currency,
                                      ).toString()
                                    : translate('noSubscriptionCost').toLowerCase(),
                                $days: isDefined(trialDuration)
                                    ? `${trialDuration.trialDurationInDays} ${Declension.days(trialDuration.trialDurationInDays)}`
                                    : translate('limitedTime'),
                            })}
                        </div>
                        <Checkbox
                            disabled={true}
                            isChecked={false}
                            onChange={() => {}}
                            label={translate('editSubscriptionTerms')}
                        />
                    </>
                )}
                <div className={styles.actionButtons}>
                    <div className={styles.primaryActionButtons}>
                        <Button
                            theme={ButtonTheme.Primary}
                            dataTest={''}
                            label={`${translate('open')} ${translate('telegram')}`}
                            onClick={() => window.open(chat.joinLink, '_blank')}
                        />
                        <Button
                            theme={ButtonTheme.Secondary}
                            dataTest={''}
                            label={`${translate('registerInChat')}`}
                            onClick={() => window.open(chat.registrationLink, '_blank')}
                        />
                    </div>
                    <Button
                        theme={ButtonTheme.Secondary}
                        dataTest={''}
                        label={translate('close')}
                        onClick={() => app.showPopup(null)}
                    />
                </div>
            </div>
        ) : (
            <>{children}</>
        );
    },
);
